/**
 * Midnight Special ordering page for the overnight shift (7PM-7AM).
 *
 * Night staff order the posted midnight special or build a salad during the
 * evening ordering window; the kitchen gets the printed list before midnight
 * service. Kept separate from HomePage: different menu, different window,
 * different vibe.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../components/auth';
import { useMidnightMenu } from '../hooks/useMidnight';
import { useSaladToppings } from '../hooks/useMenu';
import { useCreateOrder } from '../hooks/useOrders';

export function MidnightPage() {
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { data: menu, isLoading } = useMidnightMenu();
  const { toppings: saladToppings } = useSaladToppings();
  const { createOrder, loading: submitting, error: orderError } = useCreateOrder();

  const [mode, setMode] = useState<'special' | 'salad' | null>(null);
  const [selectedToppings, setSelectedToppings] = useState<number[]>([]);
  const [notes, setNotes] = useState('');
  const [confirmation, setConfirmation] = useState<string | null>(null);
  const [orderDate, setOrderDate] = useState('');

  const status = menu?.ordering_status;
  const special = menu?.special;

  // Default the picker to the earliest orderable night; snap it back into
  // range if the min moves (the 5 PM cutoff passing while the page is open)
  useEffect(() => {
    if (status?.min_date && (!orderDate || orderDate < status.min_date)) {
      setOrderDate(status.min_date);
    }
  }, [status?.min_date, orderDate]);

  // Salad-only service: the salad builder IS the menu, so open it right away
  useEffect(() => {
    if (status?.is_open && status.allow_salad && !status.offer_special) {
      setMode('salad');
    }
  }, [status?.is_open, status?.allow_salad, status?.offer_special]);

  const formatNight = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      weekday: 'long', month: 'long', day: 'numeric',
    });
  };

  const toggleTopping = (id: number) => {
    setSelectedToppings((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  const submit = async () => {
    if (!accessToken || !orderDate || !mode) return;
    const result = await createOrder(accessToken, {
      order_type: mode,
      meal_period: 'midnight',
      order_date: orderDate,
      special_id: mode === 'special' ? special?.id : undefined,
      topping_ids: mode === 'salad' ? selectedToppings : undefined,
      notes: notes.trim() || undefined,
    });
    if (result) {
      const night = formatNight(orderDate);
      setConfirmation(
        mode === 'special'
          ? `You're set — ${special?.name} at midnight, ${night}.`
          : `You're set — custom salad at midnight, ${night}.`
      );
      setSelectedToppings([]);
      setNotes('');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center text-slate-300">
        Loading tonight's menu...
      </div>
    );
  }

  // Feature off (or someone navigated here directly): send them home
  if (!status?.enabled) {
    return (
      <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center gap-4 text-slate-300 px-4">
        <p>The midnight special isn't available yet.</p>
        <button onClick={() => navigate('/')} className="text-indigo-400 underline">
          Back to lunch ordering
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 px-4 py-8">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">🌙 Midnight Special</h1>
          <Link to="/" className="text-sm text-indigo-400 hover:text-indigo-300">
            ← Day menu
          </Link>
        </div>

        <div
          className={`rounded-lg px-4 py-3 text-sm ${
            status.is_open
              ? 'bg-emerald-900/60 text-emerald-200 border border-emerald-700'
              : 'bg-slate-800 text-slate-300 border border-slate-700'
          }`}
        >
          {status.message}
        </div>

        {/* Which night? */}
        <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-5">
          <label htmlFor="midnight-date" className="block text-sm font-semibold text-slate-400 uppercase tracking-wide mb-2">
            Ordering for the night of
          </label>
          <input
            id="midnight-date"
            type="date"
            value={orderDate}
            min={status.min_date}
            max={status.max_date}
            onChange={(e) => setOrderDate(e.target.value)}
            className="rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-slate-100 focus:outline-none focus:border-indigo-500 [color-scheme:dark]"
          />
          {orderDate && (
            <p className="text-sm text-indigo-200 mt-2">
              Served at midnight, {formatNight(orderDate)}
            </p>
          )}
        </div>

        {confirmation && (
          <div className="rounded-lg bg-indigo-900/60 border border-indigo-700 px-4 py-3 text-indigo-100">
            {confirmation}
          </div>
        )}
        {orderError && (
          <div className="rounded-lg bg-red-900/60 border border-red-700 px-4 py-3 text-red-100">
            {orderError}
          </div>
        )}

        {/* Tonight's special (only when the kitchen offers one) */}
        {status.offer_special && (
        <div
          className={`rounded-xl border p-5 transition ${
            mode === 'special'
              ? 'border-indigo-400 bg-slate-800'
              : 'border-slate-700 bg-slate-800/60'
          }`}
        >
          <h2 className="text-lg font-semibold mb-1">Tonight's Special</h2>
          {special ? (
            <>
              <p className="text-xl text-indigo-200">{special.name}</p>
              {special.description && (
                <p className="text-sm text-slate-400 mt-1">{special.description}</p>
              )}
              {status.is_open && (
                <button
                  onClick={() => setMode(mode === 'special' ? null : 'special')}
                  className={`mt-4 px-4 py-2 rounded-lg font-medium ${
                    mode === 'special'
                      ? 'bg-indigo-500 text-white'
                      : 'bg-indigo-600/40 text-indigo-200 hover:bg-indigo-600/60'
                  }`}
                >
                  {mode === 'special' ? 'Selected' : 'Order the special'}
                </button>
              )}
            </>
          ) : (
            <p className="text-slate-400">
              The kitchen hasn't posted tonight's special yet.
            </p>
          )}
        </div>
        )}

        {/* Salad option */}
        {status.allow_salad && status.is_open && (
          <div
            className={`rounded-xl border p-5 transition ${
              mode === 'salad'
                ? 'border-indigo-400 bg-slate-800'
                : 'border-slate-700 bg-slate-800/60'
            }`}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Custom Salad</h2>
              <button
                onClick={() => setMode(mode === 'salad' ? null : 'salad')}
                className={`px-4 py-2 rounded-lg font-medium ${
                  mode === 'salad'
                    ? 'bg-indigo-500 text-white'
                    : 'bg-indigo-600/40 text-indigo-200 hover:bg-indigo-600/60'
                }`}
              >
                {mode === 'salad' ? 'Selected' : 'Build a salad'}
              </button>
            </div>
            {mode === 'salad' && (
              <div className="mt-4 space-y-4">
                {saladToppings?.categories.map((cat) => (
                  <div key={cat.name}>
                    <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wide mb-2">
                      {cat.name}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {cat.toppings.map((t) => (
                        <button
                          key={t.id}
                          onClick={() => toggleTopping(t.id)}
                          className={`px-3 py-1.5 rounded-full text-sm border ${
                            selectedToppings.includes(t.id)
                              ? 'bg-indigo-500 border-indigo-400 text-white'
                              : 'bg-slate-700/60 border-slate-600 text-slate-200 hover:bg-slate-700'
                          }`}
                        >
                          {t.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Notes + submit */}
        {status.is_open && mode && (
          <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-5 space-y-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Special requests (optional)
              </label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={500}
                className="w-full rounded-lg bg-slate-900 border border-slate-700 px-3 py-2 text-slate-100 focus:outline-none focus:border-indigo-500"
                placeholder="e.g. no onions"
              />
            </div>
            <button
              onClick={submit}
              disabled={submitting || !orderDate || (mode === 'salad' && selectedToppings.length === 0)}
              className="w-full py-3 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
            >
              {submitting ? 'Placing order...' : 'Place midnight order'}
            </button>
            {mode === 'salad' && selectedToppings.length === 0 && (
              <p className="text-xs text-slate-500 text-center">Pick at least one topping</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default MidnightPage;
